import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import "../ui/styles/container.css";
import error from "./utils/error.js";
import { useRef, useState, useEffect } from "react";
import { createRoot } from "react-dom/client";
import Launched from "./context.js";
import flattenTagValue from "./utils/flattenTagValue.js";
export default class Renderer {
    static formats = new Map();
    static roots = new Map();
    initialRenderOptions = new Map();
    constructor() { }
    static registerTagFormat(name, renderer) {
        if (!renderer.component)
            error("Custom renderers must have a component.");
        Renderer.formats.set(name, renderer);
    }
    renderSingleTagUI(parentTag, id, options, dry) {
        if (!parentTag || !parentTag.el.current)
            return;
        const renderTag = (parentTag, tag, childId, index) => {
            if (!tag.el.current)
                return console.error("Element is null.");
            if (Array.isArray(tag.data.value)) {
                tag.data.value.forEach((t, i) => {
                    const childEl = tag.el.current.children[i] ?? tag.el.current;
                    renderTag(parentTag, {
                        el: { current: childEl },
                        data: {
                            type: tag.data.type,
                            value: t,
                        },
                        setData: (data) => {
                            tag.setData(tag.data.value.map((v, index) => index === i
                                ? (typeof data === "function"
                                    ? data(v)
                                    : data)
                                : v));
                        },
                    }, `${id}-${i}`, i);
                });
            }
            else if (tag.data.type === "object" &&
                typeof tag.data.value === "object") {
                for (const key in tag.data.value) {
                    const childEl = tag.el.current.querySelector(`[data-key="${key}"]`);
                    if (!childEl)
                        error(`Child element with key "${key}" (under "${id}") not found. If you're using a custom renderer, make sure to add a data-key attribute to the targeted element.`);
                    renderTag(parentTag, {
                        el: { current: childEl },
                        data: {
                            type: tag.data.value[key].type,
                            value: tag.data.value[key].value,
                        },
                        setData: (data) => {
                            const newValue = typeof data === "function"
                                ? data(tag.data.value[key].value)
                                : data;
                            tag.setData({
                                ...tag.data.value,
                                [key]: {
                                    type: tag.data.value[key]
                                        .type,
                                    value: newValue,
                                },
                            });
                        },
                    }, `${childId}-${key}`, index);
                }
            }
            else {
                if (!tag.el.current)
                    return;
                const renderer = Renderer.formats.get(tag.data.type);
                if (!renderer) {
                    return console.warn(`No renderer found for tag type "${tag.data.type}".`);
                }
                if (renderer.parentValidator &&
                    !renderer.parentValidator(tag.el.current)) {
                    return console.warn(`Parent element of tag "${childId}" does not satisfy the constraints of the "${tag.data.type}" renderer.`);
                }
                const id = `Lt-${childId.replaceAll(" ", "-")}`;
                let userOptions;
                if (this.initialRenderOptions.get(id))
                    userOptions = this.initialRenderOptions.get(id);
                else {
                    userOptions = {
                        arrayMutable: options?.arrayMutable ?? false,
                        index,
                        parentTag,
                    };
                    this.initialRenderOptions.set(id, userOptions);
                }
                if (dry)
                    return;
                const existingNode = document.getElementById(id);
                if (existingNode)
                    existingNode.remove();
                setTimeout(() => {
                    if (Renderer.roots.get(childId)) {
                        Renderer.roots.get(childId).unmount();
                        Renderer.roots.delete(childId);
                    }
                    const rootNode = document.createElement("div");
                    rootNode.id = id;
                    tag.el.current.appendChild(rootNode);
                    const root = createRoot(rootNode);
                    Renderer.roots.set(childId, root);
                    const t = {
                        ...tag,
                        data: {
                            type: tag.data.type,
                            value: flattenTagValue(tag.data.value),
                        },
                    };
                    root.render(_jsx(TagUI, { tag: t, renderer: renderer, id: childId, options: userOptions }));
                }, 0);
            }
        };
        renderTag(parentTag, parentTag, id, 0);
    }
    unmountSingleTagUI(tagId) {
        const id = `Lt-${tagId.split(" ").join("-")}`;
        const root = Renderer.roots.get(tagId);
        if (root) {
            root.unmount();
            Renderer.roots.delete(tagId);
        }
        const node = document.getElementById(id);
        if (node)
            node.remove();
    }
}
function TagUI({ tag, renderer, id, options, }) {
    const containerRef = useRef(null);
    const [selected, setSelected] = useState(false);
    const { arrayMutable, parentTag, index } = options;
    function close() {
        setSelected(false);
        containerRef.current?.blur();
        renderer.onClose?.({
            element: tag.el.current ?? undefined,
        });
        Launched.events.emit("tag:deselect", id, tag);
    }
    function updateData(data) {
        tag.setData(data);
        // @ts-expect-error
        options.parentTag.el.current = null;
    }
    function duplicateTagItem() {
        if (!Array.isArray(parentTag.data.value))
            return;
        parentTag.setData((p) => [
            ...p.slice(0, index + 1),
            p[index],
            ...p.slice(index + 1),
        ]);
    }
    function removeTagItem() {
        if (!Array.isArray(parentTag.data.value))
            return;
        parentTag.setData((p) => [
            ...p.slice(0, index),
            ...p.slice(index + 1),
        ]);
    }
    function onTagSelect(selectedId) {
        if (selectedId !== id)
            setSelected(false);
    }
    useEffect(() => {
        if (!tag.el.current)
            error("Element is null.");
        tag.el.current.classList.add("tagged");
        if (getComputedStyle(tag.el.current).position === "static") {
            tag.el.current.style.position = "relative";
        }
        Launched.events.emit("tag:mount", id, tag);
        Launched.events.on("tag:select", onTagSelect);
        return () => {
            Launched.events.emit("tag:unmount", id, tag);
            Launched.events.off("tag:select", onTagSelect);
        };
    }, []);
    useEffect(() => {
        const state = {
            element: tag.el.current ?? undefined,
            data: tag.data.value,
        };
        renderer.onDataUpdate?.(state);
        if (Launched.instance.config.mode === "static" && state.element) {
            const changed = renderer.getStaticProperties?.(state.element) !== state.data;
            if (changed)
                renderer.updateStaticProperties?.(state);
        }
    }, [tag.data.value]);
    function select() {
        if (selected)
            return;
        setSelected(true);
        renderer?.onSelect?.({ element: tag.el.current });
        Launched.events.emit("tag:select", id, tag);
    }
    if (!tag.el.current)
        return null;
    return (_jsxs("div", { ref: containerRef, tabIndex: 0, onMouseDown: select, onFocus: select, className: `Launched__tag-container ${selected && "active"}`, children: [_jsx(renderer.component, { element: tag.el.current, value: tag.data.value, selected: selected, updateData: updateData, close: close, id: id, context: Launched.instance }), arrayMutable && (_jsxs("div", { onMouseDown: (e) => e.preventDefault(), className: "Launched__tag-arrayControls Launched__toolbar-tools", children: [_jsx("button", { className: "Launched__toolbar-button add", onClick: duplicateTagItem, children: _jsxs("svg", { viewBox: "0 0 24 24", className: "Launched__icon", children: [_jsx("rect", { x: "9", y: "9", width: "13", height: "13", rx: "2", ry: "2" }), _jsx("path", { d: "M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" })] }) }), _jsx("button", { className: "Launched__button remove", onClick: removeTagItem, children: _jsxs("svg", { viewBox: "0 0 24 24", className: "Launched__icon", children: [_jsx("polyline", { points: "3 6 5 6 21 6" }), _jsx("path", { d: "M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" })] }) })] }))] }));
}
